import {
  buildMemberName,
  formatSerialDisplay,
  getBaseGymMemberId,
  getGymMemberId,
  getMemberPublicSerial,
  getMembershipSerialNo,
  isPendingGymMemberId,
  stripMemberNameTitle,
} from '../../pages/admin/members/memberData';
import { membershipPendingAmount, membershipTotalAmount } from '../../utils/membershipFees';
import { sumPaymentSplits } from '../../utils/splitPaymentUtils';

function sanitizeProfile(profile) {
  const clean = {};
  Object.entries(profile).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (value instanceof File) return;
    clean[key] = value;
  });
  return clean;
}

export function rowToMember(row) {
  const profile = row.profile || {};
  const addressFromProfile = [profile.addressLine1, profile.addressLine2, profile.city, profile.state, profile.zipCode]
    .filter(Boolean)
    .join(', ');

  return {
    ...profile,
    id: row.id,
    gymId: isPendingGymMemberId(row.gym_member_id || profile.gymId)
      ? ''
      : (row.gym_member_id || profile.gymId || ''),
    name: stripMemberNameTitle(row.full_name || buildMemberName(profile) || 'Member') || 'Member',
    phone: row.phone || profile.mobile || profile.phone || '',
    email: row.email || profile.email || '',
    dob: row.dob || profile.dob || '',
    gender: row.gender || profile.gender || 'male',
    address: row.address || addressFromProfile || profile.address || '',
    country: row.country || profile.country || '',
    emergencyContact: row.emergency_contact || profile.emergencyContact || '',
    cnic: profile.cnic || row.cnic || '',
    package: row.package || profile.package || 'Basic',
    trainer: row.trainer || profile.trainer || 'Unassigned',
    branch: row.branch || profile.branch || '',
    joiningDate: row.joining_date || profile.joiningDate || '',
    // Prefer the user-set joining/created date over DB insert timestamp
    createdAt: row.joining_date
      || profile.joiningDate
      || profile.createdAt
      || (row.created_at ? String(row.created_at).slice(0, 10) : '')
      || '',
    status: row.status || profile.status || 'active',
    isWalkIn: Boolean(profile.is_walk_in)
      || String(row.gym_member_id || '').trim() === '1',
  };
}

export function formToMemberRow(form, gymId) {
  const name = buildMemberName(form);
  const today = new Date().toISOString().slice(0, 10);
  const address = [form.addressLine1, form.addressLine2, form.city, form.state, form.zipCode]
    .filter(Boolean)
    .join(', ') || form.address || null;
  const branch = (form.branch || '').trim() || null;

  return {
    gym_id: gymId,
    gym_member_id: getBaseGymMemberId(form) || (
      String(form.gymId || '').trim()
        && !/^\d+$/.test(String(form.gymId).trim())
        && !/^id[\s\-:_]*\d+/i.test(String(form.gymId).trim())
        ? String(form.gymId).trim()
        : form.gymId
    ) || null,
    full_name: name || 'Member',
    phone: form.mobile || form.phone || null,
    email: form.email || null,
    branch,
    joining_date: form.createdAt || form.joiningDate || today,
    status: form.status || 'active',
    profile: sanitizeProfile({
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      mobile: form.mobile,
      phone: form.mobile || form.phone,
      alternateMobile: form.alternateMobile,
      landline: form.landline,
      cnic: form.cnic,
      dob: form.dob,
      gender: form.gender,
      address,
      addressLine1: form.addressLine1,
      addressLine2: form.addressLine2,
      city: form.city,
      state: form.state,
      zipCode: form.zipCode,
      country: form.country,
      photoPreview: typeof form.photoPreview === 'string' ? form.photoPreview : null,
      package: form.package || 'Basic',
      trainer: form.trainer || 'Unassigned',
      branch,
      emergencyContact: form.emergencyContact || null,
      joiningDate: form.createdAt || form.joiningDate || today,
      createdAt: form.createdAt || form.joiningDate || today,
      status: form.status || 'active',
    }),
  };
}

export function rowToMembership(row, member, feeInfo = {}) {
  const frozen = Boolean(row.frozen);
  const status = frozen ? 'inactive' : row.status;
  const startDate = row.start_date || '';
  const profile = member?.profile || {};
  const addressFromProfile = [profile.addressLine1, profile.addressLine2, profile.city, profile.state, profile.zipCode]
    .filter(Boolean)
    .join(', ');
  // Prefer member public serial (e.g. Members list ID-5421) over a mismatched
  // package serial on the membership row (e.g. 5426).
  const memberPublicSerial = getMemberPublicSerial(member);
  const rowSerial = row.serial_no != null && row.serial_no !== ''
    ? (Number.isFinite(Number(row.serial_no)) ? Number(row.serial_no) : String(row.serial_no).trim())
    : null;
  const serialNo = memberPublicSerial != null
    ? memberPublicSerial
    : (rowSerial != null ? rowSerial : getMembershipSerialNo(member));
  const baseGymId = getBaseGymMemberId({
    ...member,
    gym_member_id: member?.gym_member_id || member?.gymId,
    baseGymId: member?.baseGymId,
    serialNo: null,
  }) || member?.gym_member_id || '';
  const displayId = serialNo != null ? formatSerialDisplay(serialNo) : '';
  const complimentary = Boolean(row.complimentary)
    || String(feeInfo.paymentStatus || '').toLowerCase() === 'complimentary'
    || String(status || '').toLowerCase() === 'complimentary';
  const packageFee = complimentary ? 0 : (Number(feeInfo.packageFee) || 0);
  const registrationFee = complimentary ? 0 : (Number(feeInfo.registrationFee) || 0);
  const discountAmount = complimentary ? 0 : (Number(feeInfo.discountAmount) || 0);
  const personalTrainingFee = complimentary ? 0 : (Number(feeInfo.personalTrainingFee) || 0);
  const classesFee = complimentary ? 0 : (Number(feeInfo.classesFee) || 0);
  const totalPaid = complimentary ? 0 : (Number(feeInfo.totalPaid) || 0);
  return {
    id: row.id,
    member: stripMemberNameTitle(member?.full_name || member?.name || '') || '—',
    baseGymId: baseGymId || '',
    gymId: displayId || '—',
    phone: member?.phone || profile.mobile || profile.phone || '—',
    email: member?.email || profile.email || '—',
    cnic: profile.cnic || member?.cnic || '—',
    gender: member?.gender || profile.gender || '—',
    dob: member?.dob || profile.dob || '—',
    address: member?.address || addressFromProfile || profile.address || '—',
    trainer: member?.trainer || profile.trainer || '—',
    branch: member?.branch || row.branch || row.branch_name || profile.branch || '',
    package: row.package_name,
    duration: row.duration || '',
    serialNo,
    start: startDate,
    startDate,
    expiry: row.expiry_date,
    datesTbc: Boolean(row.dates_tbc),
    complimentary,
    status,
    frozen,
    autoRenewal: Boolean(row.auto_renewal),
    lifetime: Boolean(row.lifetime),
    packageFee,
    registrationFee,
    discountAmount,
    personalTrainingFee,
    classesFee,
    totalPaid,
    totalAmount: complimentary
      ? 0
      : membershipTotalAmount({
        packageFee,
        registrationFee,
        discountAmount,
        personalTrainingFee,
        classesFee,
      }),
    pendingAmount: complimentary
      ? 0
      : membershipPendingAmount({
        packageFee,
        registrationFee,
        discountAmount,
        personalTrainingFee,
        classesFee,
        totalPaid,
        pendingAmount: feeInfo.pendingAmount,
      }),
    paymentSplits: complimentary
      ? []
      : (Array.isArray(feeInfo.paymentSplits) ? feeInfo.paymentSplits : []),
    paymentDbId: complimentary ? null : (feeInfo.paymentDbId || null),
    paymentId: complimentary ? null : (feeInfo.paymentRef || null),
    invoiceId: complimentary ? null : (feeInfo.invoiceId || null),
    paymentStatus: complimentary ? 'complimentary' : (feeInfo.paymentStatus || ''),
    paymentDate: feeInfo.paymentDate || '',
    paymentNote: feeInfo.paymentNote || '',
    paymentSource: feeInfo.paymentSource || 'subscription',
    memberId: row.member_id,
    joiningDate: member?.joining_date || member?.joiningDate || '',
    createdAt: row.created_at ? String(row.created_at).slice(0, 10) : '',
  };
}

export function rowToFreeze(row, member) {
  const isActiveFreeze = row.status === 'active';
  return {
    id: row.id,
    member: member?.full_name || '—',
    gymId: getGymMemberId(member) || '—',
    memberId: row.member_id,
    startDate: row.start_date,
    endDate: row.end_date,
    reason: row.reason || '',
    autoExtend: row.auto_extend,
    status: row.status,
    accountStatus: isActiveFreeze ? 'inactive' : 'active',
  };
}

export function rowToAttendance(row, member, trainer) {
  const isMember = row.person_type === 'member';
  const branch = (
    row.branch
    || row.branch_name
    || (isMember ? member?.branch : trainer?.branch)
    || member?.branch
    || trainer?.branch
    || ''
  );
  return {
    id: row.id,
    member: isMember ? (member?.full_name || '—') : (trainer?.full_name || '—'),
    personType: row.person_type,
    memberId: row.member_id,
    trainerId: row.trainer_id,
    branch: branch || '—',
    gymId: isMember ? (getGymMemberId(member) || '—') : '—',
    date: row.attendance_date || row.date || row.log_date || row.created_at?.slice?.(0, 10) || '',
    checkIn: row.check_in,
    checkOut: row.check_out,
    status: row.status,
  };
}

const PAYMENT_CUSTOMER_PREFIX = 'Customer: ';

function customerFromPaymentNote(note) {
  if (!note?.startsWith(PAYMENT_CUSTOMER_PREFIX)) return null;
  const rest = note.slice(PAYMENT_CUSTOMER_PREFIX.length);
  const sep = rest.indexOf(' — ');
  return (sep >= 0 ? rest.slice(0, sep) : rest).trim() || null;
}

export function rowToPayment(row, member) {
  const packageFee = Number(row.package_price) || 0;
  const registrationFee = Number(row.registration_fee) || 0;
  const discount = Number(row.discount_amount) || 0;
  const pt = Number(row.personal_training_fee) || 0;
  const classes = Number(row.classes_fee) || 0;
  const headerPaid = Number(row.amount) || 0;
  const splits = Array.isArray(row.payment_splits) ? row.payment_splits : [];
  const splitsPaid = sumPaymentSplits(splits);
  // Pending / partial rows sometimes store 0 in amount while splits hold the cash taken
  const paid = headerPaid > 0 ? headerPaid : splitsPaid;
  const totalDue = Math.max(0, packageFee - discount + registrationFee + pt + classes);
  const hasFeeBreakdown = packageFee > 0 || registrationFee > 0 || pt > 0 || classes > 0 || discount > 0;
  const pendingAmount = hasFeeBreakdown
    ? Math.max(0, Math.round((totalDue - paid) * 100) / 100)
    : 0;

  const notePhone = String(row.payment_note || '').match(/Phone:\s*([^·|]+)/i)?.[1]?.trim() || '';
  const phone = String(
    member?.phone
    || member?.mobile
    || row.phone
    || notePhone
    || '',
  ).trim();

  return {
    id: row.payment_ref || row.id,
    // Prefer payment.member_name (kept in sync on member rename + editable on sale edit)
    member: stripMemberNameTitle(
      row.member_name || member?.full_name || customerFromPaymentNote(row.payment_note) || '',
    ) || '—',
    gymId: getGymMemberId(member) || '—',
    phone: phone || '—',
    mobile: phone || '—',
    package: row.package_name || '—',
    amount: paid,
    packagePrice: packageFee,
    registrationFee,
    discountAmount: discount,
    personalTrainingFee: pt,
    classesFee: classes,
    pendingAmount,
    method: row.payment_method,
    paymentSplits: splits,
    date: row.paid_at,
    paidAt: row.paid_at || '',
    status: row.status,
    invoiceId: row.invoice_id,
    paymentNote: row.payment_note || '',
    source: row.source || 'subscription',
    memberId: row.member_id,
    rawId: row.id,
    startDate: row.start_date || '',
    endDate: row.end_date || '',
    duration: row.duration || '',
    createdAt: row.created_at || '',
  };
}

export function rowToGuestPass(row) {
  const createdAt = row.created_at
    ? (String(row.created_at).match(/^\d{4}-\d{2}-\d{2}/)?.[0] || String(row.created_at).slice(0, 10))
    : '';
  const startDate = row.start_date ? String(row.start_date).slice(0, 10) : '';
  // Prefer visit start_date for day reports (imports often share one created_at batch day)
  const saleDate = startDate || createdAt || '';
  return {
    id: row.id,
    name: row.guest_name,
    phone: row.phone,
    duration: row.duration || '',
    passType: row.pass_type,
    startDate: row.start_date,
    endDate: row.end_date || '',
    fee: Number(row.fee) || 0,
    referredBy: row.referred_by_name || '',
    referredByGymId: row.referred_by_gym_id || '',
    referredByMemberId: row.referred_by_member_id,
    createdAt,
    date: saleDate,
  };
}

export function rowToLocker(row) {
  return {
    id: row.locker_code,
    dbId: row.id,
    member: row.member_name || '—',
    memberId: row.member_id,
    size: row.size || 'Medium',
    status: row.status,
    expiry: row.expiry_date || '—',
  };
}

export function rowToEquipment(row) {
  return {
    id: row.id,
    name: row.name,
    companyName: row.company_name || '',
    category: row.category || '',
    location: row.location || '',
    quantity: Math.max(1, Number(row.quantity) || 1),
    status: row.status || 'available',
    remarks: row.remarks || '',
  };
}

export function rowToClass(row) {
  return {
    id: row.id,
    name: row.name,
    startDate: row.start_date || '',
    endDate: row.end_date || '',
    description: row.description || '',
    fee: Number(row.fee) || 0,
  };
}

export function rowToTrainer(row) {
  return {
    id: row.id,
    name: row.full_name,
    email: row.email || '',
    phone: row.phone || '',
    specialization: row.specialization || '',
    branch: row.branch || row.branch_name || '',
    salary: Number(row.salary) || 0,
    status: row.status || 'active',
    certifications: row.certifications
      ? row.certifications.split('\n').filter(Boolean)
      : [],
    assignedMembers: Array.isArray(row.assigned_members)
      ? row.assigned_members
      : Array.isArray(row.assignedMembers)
        ? row.assignedMembers
        : [],
    reports: Array.isArray(row.reports) ? row.reports : [],
  };
}

export function rowToReceptionist(row) {
  return {
    id: row.id,
    name: row.full_name,
    email: row.email || '',
    phone: row.phone || '',
    branch: row.branch_name || row.branch || '',
    branchId: row.branch_id || null,
    shift: row.shift || '',
    status: row.status || 'active',
    permissions: Array.isArray(row.permissions) ? row.permissions : [],
    hasLogin: Boolean(row.has_login),
  };
}

export function rowToStaffProfile(row) {
  return {
    id: row.id,
    staffId: row.staff_id,
    staffType: row.staff_type || 'receptionist',
    name: row.full_name || '',
    email: row.email || '',
    phone: row.phone || '',
  };
}

export function rowToPackage(row, memberCount) {
  const liveCount = memberCount !== undefined && memberCount !== null
    ? Number(memberCount) || 0
    : (Number(row.members) || 0);
  const serialRaw = row.serial_no_start ?? row.serialNoStart ?? row.serial_no ?? row.serialNo;
  const serialStart = Number(serialRaw);
  return {
    id: row.id,
    name: row.name,
    duration: row.duration,
    price: Number(row.price) || 0,
    registrationFee: Number(row.registration_fee) || 0,
    tax: Number(row.tax) || 0,
    serialNo: Number.isFinite(serialStart) && serialStart > 0 ? serialStart : '',
    benefits: row.benefits || '',
    status: row.status,
    branch: row.branch_name || '',
    branchId: row.branch_id || null,
    freezeOption: row.freeze_option ?? true,
    autoRenewal: row.auto_renewal ?? false,
    members: liveCount,
  };
}
